import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, User, Search, Share2, MoreHorizontal, MoreVertical, Pencil, Trash2, Eye } from 'lucide-react';
import useStore from '@/store/useStore';
import { EntityActions } from '@/components/crud/EntityActions';
import { toast } from '@/components/ui/use-toast';
import ViewToggle from '@/components/common/ViewToggle';
import BulkImport from '@/components/common/BulkImport';
import SortableTableHeader from '@/components/common/SortableTableHeader';
import UniqueIdDisplay from '@/components/common/UniqueIdDisplay';
import BulkActions from '@/components/common/BulkActions';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Player, ImportPlayer } from '@/types/cricket';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const PlayersPage = () => {
  const { 
    teams, 
    players, 
    fetchTeams, 
    fetchPlayers, 
    updatePlayer,
    deletePlayer,
    addPlayer,
    loading 
  } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterTeamId, setFilterTeamId] = useState<string>('all');
  const [view, setView] = useState<'card' | 'list'>('card');
  const [sortKey, setSortKey] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchTeams(), fetchPlayers()]);
        console.log("Teams loaded:", teams);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, [fetchTeams, fetchPlayers]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Derive unique filter options
  const uniqueRoles = Array.from(new Set(players.map(p => p.role).filter(r => r !== undefined && r !== null && r !== '')))
    .sort((a, b) => a!.localeCompare(b!));

  const teamFilterOptions = React.useMemo(() => {
    // Map over players and safely get the first teamId
    const playerTeamIds = new Set(players.flatMap(p => p.teamIds || [])); // Use flatMap to handle multiple teamIds per player
    const options: { id: string; name: string }[] = [];

    // Add 'all' option at the beginning
    options.push({ id: 'all', name: 'All Teams' });

    playerTeamIds.forEach(teamId => {
      if (teamId === null || teamId === undefined || teamId === '') { // Handle undefined/null/empty string teamIds
        options.push({ id: 'UNKNOWN_TEAM_NULL', name: 'Unknown Team' });
      } else {
        const team = teams.find(t => t.id === teamId);
        if (team) {
          options.push({ id: team.id, name: team.name });
        } else {
          options.push({ id: `INVALID_TEAM_ID_${teamId}`, name: `Unknown Team (ID: ${teamId})` });
        }
      }
    });

    // Remove duplicates based on ID and sort by name (excluding 'All Teams' from sorting)
    const distinctOptions = Array.from(new Map(options.map(item => [item.id, item])).values());
    return distinctOptions.sort((a, b) => {
      if (a.id === 'all') return -1; // Keep 'All Teams' at the top
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' })
    });
  }, [players, teams]);

  const sortedPlayers = React.useMemo(() => {
    const filtered = players.filter(player => {
      // Text Search
      const playerName = player.name.toLowerCase();
      const playerRole = player.role.toLowerCase();
      const playerUniqueId = player.uniqueId?.toLowerCase() || '';
      // Safely access the first teamId for filtering
      const playerFirstTeamId = player.teamIds && player.teamIds.length > 0 ? player.teamIds[0] : null;
      const teamName = teams.find(t => t.id === playerFirstTeamId)?.name?.toLowerCase() || '';

      const searchLower = searchTerm.toLowerCase();
      const textMatches =
        playerName.includes(searchLower) ||
        playerRole.includes(searchLower) ||
        playerUniqueId.includes(searchLower) ||
        teamName.includes(searchLower);

      if (searchTerm && !textMatches) return false;

      // Role Filter
      if (filterRole !== 'all' && player.role !== filterRole) return false;

      // Team Filter
      if (filterTeamId !== 'all') {
        // Handle null/undefined/empty string teamId for players without a team
        if (filterTeamId === 'UNKNOWN_TEAM_NULL') {
          if (playerFirstTeamId === null || playerFirstTeamId === undefined || playerFirstTeamId === '') return true; // Match players with no team
          return false;
        } else if (filterTeamId.startsWith('INVALID_TEAM_ID_')) {
          // For invalid team IDs, match players whose first teamId is explicitly null or undefined,
          // or whose first teamId matches the invalid ID part
          const invalidIdPart = filterTeamId.replace('INVALID_TEAM_ID_', '');
          return playerFirstTeamId === invalidIdPart;
        } else {
          // Match players with the selected valid teamId
          return playerFirstTeamId === filterTeamId;
        }
      }

      return true;
    });

    if (sortKey) {
      filtered.sort((a, b) => {
        let aValue: string | number | boolean | string[] | undefined;
        let bValue: string | number | boolean | string[] | undefined;

        if (sortKey === 'teamId') {
          aValue = (a as Player).teamIds && (a as Player).teamIds.length > 0 ? (a as Player).teamIds[0] : '';
          bValue = (b as Player).teamIds && (b as Player).teamIds.length > 0 ? (b as Player).teamIds[0] : '';
        } else if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
          // Convert date strings to timestamps for proper numeric comparison
          aValue = a[sortKey] ? new Date(a[sortKey] as string).getTime() : 0;
          bValue = b[sortKey] ? new Date(b[sortKey] as string).getTime() : 0;
          // Direct numeric comparison for dates
          return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
        } else {
          aValue = (a as Player)[sortKey as keyof Player];
          bValue = (b as Player)[sortKey as keyof Player];
        }

        let finalAValue = Array.isArray(aValue) ? aValue.join('') : aValue;
        let finalBValue = Array.isArray(bValue) ? bValue.join('') : bValue;

        if (finalAValue === undefined || finalAValue === null) finalAValue = '';
        if (finalBValue === undefined || finalBValue === null) finalBValue = '';

        let comparison = 0;

        if (typeof finalAValue === 'string' && typeof finalBValue === 'string') {
          comparison = finalAValue.localeCompare(finalBValue, 'en-US', { sensitivity: 'base' });
        } else if (typeof finalAValue === 'number' && typeof finalBValue === 'number') {
          comparison = finalAValue - finalBValue;
        } else {
          // Fallback for other types, treat as strings
          comparison = String(finalAValue).localeCompare(String(finalBValue), 'en-US', { sensitivity: 'base' });
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [players, searchTerm, filterRole, filterTeamId, sortKey, sortDirection, teams]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPlayers(sortedPlayers.map(p => p.id));
    } else {
      setSelectedPlayers([]);
    }
  };

  const handleSelectPlayer = (playerId: string, checked: boolean) => {
    if (checked) {
      setSelectedPlayers([...selectedPlayers, playerId]);
    } else {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
    }
  };

  const handleBulkDelete = async () => {
    for (const playerId of selectedPlayers) {
      try {
        await deletePlayer(playerId);
      } catch (error) {
        console.error('Error deleting player:', error);
      }
    }
    setSelectedPlayers([]);
  };

  const handleBulkEdit = () => {
    console.log('Bulk edit players:', selectedPlayers);
  };

  const hasTeams = teams.length > 0;

  const handleBulkImport = async (data: ImportPlayer[]) => {
    for (const row of data) {
      try {
        const team = teams.find(t => t.uniqueId === row.teamId);
        // Helper to parse values
        const parseValue = (val: unknown, type: 'string' | 'number' | 'boolean' | 'date') => {
          if (val === undefined || val === null || val === '' || val === 'N/A') return undefined;
          if (type === 'number') return isNaN(Number(val)) ? undefined : Number(val);
          if (type === 'boolean') return val === true || val === 'true' || val === 'TRUE' ? true : false;
          if (type === 'date') return isNaN(Date.parse(val as string)) ? undefined : val as string;
          return val as string;
        };
        // Type-safe union helpers
        const battingStyle = ['left-handed', 'right-handed'].includes((row.batting_style || '').toLowerCase()) ? (row.batting_style?.toLowerCase() as 'left-handed' | 'right-handed') : undefined;
        const battingOrder = ['opener', 'top-order', 'middle-order', 'lower-order'].includes((row.batting_order || '').toLowerCase()) ? (row.batting_order?.toLowerCase() as 'opener' | 'top-order' | 'middle-order' | 'lower-order') : undefined;
        const bowlingStyle = ['fast', 'medium', 'spin'].includes((row.bowling_style || '').toLowerCase()) ? (row.bowling_style?.toLowerCase() as 'fast' | 'medium' | 'spin') : undefined;
        const bowlingArm = ['left-arm', 'right-arm'].includes((row.bowling_arm || '').toLowerCase()) ? (row.bowling_arm?.toLowerCase() as 'left-arm' | 'right-arm') : undefined;
        const bowlingType = ['pace', 'off-spin', 'leg-spin', 'left-arm-orthodox', 'left-arm-unorthodox'].includes((row.bowling_type || '').toLowerCase()) ? (row.bowling_type?.toLowerCase() as 'pace' | 'off-spin' | 'leg-spin' | 'left-arm-orthodox' | 'left-arm-unorthodox') : undefined;
        const keepingStyle = ['conventional', 'aggressive'].includes((row.keeping_style || '').toLowerCase()) ? (row.keeping_style?.toLowerCase() as 'conventional' | 'aggressive') : undefined;
        const fieldingStyle = ['aggressive', 'balanced', 'defensive'].includes((row.fielding_style || '').toLowerCase()) ? (row.fielding_style?.toLowerCase() as 'aggressive' | 'balanced' | 'defensive') : undefined;
        const injuryStatus = ['fit', 'injured', 'recovering'].includes((row.injury_status || '').toLowerCase()) ? (row.injury_status?.toLowerCase() as 'fit' | 'injured' | 'recovering') : undefined;
        const gender = ['male', 'female', 'other'].includes((row.gender || '').toLowerCase()) ? (row.gender?.toLowerCase() as 'male' | 'female' | 'other') : undefined;
        const isCaptain = parseValue(row.is_captain, 'boolean') as boolean | undefined;
        await addPlayer({
          name: row.name || '',
          role: row.role || 'batsman',
          teamIds: team?.id ? [team.id] : [],
          isCaptain,
          battingStyle,
          battingOrder,
          bowlingStyle,
          bowlingArm,
          bowlingType,
          keepingStyle,
          fieldingPosition: parseValue(row.fielding_position, 'string') as string | undefined,
          fieldingStyle,
          injuryStatus,
          matchFitness: parseValue(row.match_fitness, 'number') as number | undefined,
          recentForm: parseValue(row.recent_form, 'string') as string | undefined,
          careerRuns: parseValue(row.career_runs, 'number') as number | undefined,
          careerMatchesPlayed: parseValue(row.career_matches_played, 'number') as number | undefined,
          careerInningsPlayed: parseValue(row.career_innings_played, 'number') as number | undefined,
          careerStrikeRate: parseValue(row.career_strike_rate, 'number') as number | undefined,
          careerAverage: parseValue(row.career_average, 'number') as number | undefined,
          highestScore: parseValue(row.highest_score, 'number') as number | undefined,
          notOuts: parseValue(row.not_outs, 'number') as number | undefined,
          fours: parseValue(row.fours, 'number') as number | undefined,
          sixes: parseValue(row.sixes, 'number') as number | undefined,
          careerWickets: parseValue(row.career_wickets, 'number') as number | undefined,
          careerOversBowled: parseValue(row.career_overs_bowled, 'number') as number | undefined,
          careerEconomyRate: parseValue(row.career_economy_rate, 'number') as number | undefined,
          careerBowlingAverage: parseValue(row.career_bowling_average, 'number') as number | undefined,
          bestBowlingFigures: parseValue(row.best_bowling_figures, 'string') as string | undefined,
          maidenOvers: parseValue(row.maiden_overs, 'number') as number | undefined,
          catches: parseValue(row.catches, 'number') as number | undefined,
          runOuts: parseValue(row.run_outs, 'number') as number | undefined,
          stumpings: parseValue(row.stumpings, 'number') as number | undefined,
          dateOfBirth: parseValue(row.date_of_birth, 'date') as string | undefined,
          placeOfBirth: parseValue(row.place_of_birth, 'string') as string | undefined,
          nationality: parseValue(row.nationality, 'string') as string | undefined,
          heightCm: parseValue(row.height_cm, 'number') as number | undefined,
          weightKg: parseValue(row.weight_kg, 'number') as number | undefined,
          jerseyNumber: parseValue(row.jersey_number, 'number') as number | undefined,
          nickname: parseValue(row.nickname, 'string') as string | undefined,
          gender,
          country: parseValue(row.country, 'string') as string | undefined,
          logoColor: parseValue(row.shadow_logo_color, 'string') as string | undefined,
        });
      } catch (error) {
        console.error('Error importing player:', error);
      }
    }
  };

  const allPlayerCsvHeaders = [
    'name', 'role', 'teamId', 'isCaptain', 'battingStyle', 'battingOrder',
    'bowlingStyle', 'bowlingArm', 'bowlingType', 'keepingStyle',
    'fieldingPosition', 'fieldingStyle', 'injuryStatus', 'matchFitness',
    'recentForm', 'careerRuns', 'careerMatchesPlayed', 'careerInningsPlayed',
    'careerStrikeRate', 'careerAverage', 'highestScore', 'notOuts', 'fours',
    'sixes', 'careerWickets', 'careerOversBowled', 'careerEconomyRate',
    'careerBowlingAverage', 'bestBowlingFigures', 'maidenOvers', 'catches',
    'runOuts', 'stumpings', 'dateOfBirth', 'placeOfBirth', 'nationality',
    'heightCm', 'weightKg', 'jerseyNumber', 'nickname', 'gender', 'country'
  ];
  

  const samplePlayerData = [
    { 
      name: 'Steve Smith', 
      role: 'batsman', 
      teamId: 'TM-AUS001',
      isCaptain: 'false',
      battingStyle: 'right-handed',
      battingOrder: 'top-order',
      bowlingStyle: 'leg spin',
      bowlingArm: 'right-arm',
      bowlingType: 'legbreak',
      keepingStyle: '',
      fieldingPosition: 'slip',
      fieldingStyle: 'conventional',
      injuryStatus: 'fit',
      matchFitness: '98',
      recentForm: 'excellent',
      careerRuns: '9500',
      careerMatchesPlayed: '180',
      careerInningsPlayed: '160',
      careerStrikeRate: '87.3',
      careerAverage: '58.4',
      highestScore: '239',
      notOuts: '18',
      fours: '900',
      sixes: '80',
      careerWickets: '30',
      careerOversBowled: '120.4',
      careerEconomyRate: '5.8',
      careerBowlingAverage: '45.6',
      bestBowlingFigures: '3/18',
      maidenOvers: '8',
      catches: '170',
      runOuts: '15',
      stumpings: '0',
      dateOfBirth: '1989-06-02',
      placeOfBirth: 'Sydney',
      nationality: 'Australian',
      heightCm: '176',
      weightKg: '78',
      jerseyNumber: '49',
      nickname: 'Smudge',
      gender: 'male',
      country: 'Australia'
    }
  ];
  
  
  const requiredPlayerColumns = ['name', 'role', 'teamId'];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Players</h1>
          <p className="text-gray-500">Manage cricket players</p>
        </div>
        <div className="flex gap-2">
          <BulkImport 
            type="players"
            onImport={handleBulkImport}
            sampleData={samplePlayerData}
            headers={allPlayerCsvHeaders}
            requiredColumns={requiredPlayerColumns}
          />
          <Link to="/players/new">
            <Button 
              className="bg-cricket-green"
              onClick={(e) => {
                if (!hasTeams) {
                  e.preventDefault();
                  toast({
                    title: "Cannot create players yet",
                    description: "Please create at least one team first.",
                    variant: "destructive",
                  });
                }
              }}
            >
              <Plus size={16} className="mr-2" />
              Add Player
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative flex-[2] w-full">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 w-full"
          />
        </div>
        <Select onValueChange={setFilterRole} value={filterRole}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {uniqueRoles.map(role => (
              <SelectItem key={role} value={role}>{role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select onValueChange={setFilterTeamId} value={filterTeamId}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent>
            {teamFilterOptions.map(team => (
              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleSort('name')} className="flex items-center justify-between">
              <span>Name</span>
              {sortKey === 'name' && (
                <span className="ml-2 text-muted-foreground">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort('createdAt')} className="flex items-center justify-between">
              <span>Created Date</span>
              {sortKey === 'createdAt' && (
                <span className="ml-2 text-muted-foreground">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSort('updatedAt')} className="flex items-center justify-between">
              <span>Modified Date</span>
              {sortKey === 'updatedAt' && (
                <span className="ml-2 text-muted-foreground">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}>
              Toggle Sort Direction
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ViewToggle view={view} onViewChange={setView} />
      </div>

      {selectedPlayers.length > 0 && view === 'list' && (
        <BulkActions
          selectedCount={selectedPlayers.length}
          onDelete={handleBulkDelete}
          onEdit={handleBulkEdit}
          entityType="player"
        />
      )}

      {sortedPlayers.length > 0 ? (
        view === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPlayers.map((player) => {
              const playerFirstTeamId = player.teamIds && player.teamIds.length > 0 ? player.teamIds[0] : null;
              const team = teams.find(t => t.id === playerFirstTeamId);
              return (
                <Card key={player.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start flex-wrap">
                      <div className="flex-1 flex items-center gap-4 min-w-0">
                        {player.profilePictureUrl ? (
                          <img
                            src={player.profilePictureUrl}
                            alt={player.name + ' profile picture'}
                            className="w-12 h-12 object-cover rounded-full border-2 border-gray-200 bg-white shadow-sm"
                          />
                        ) : (
                          <div
                            className="flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-xl uppercase select-none"
                            style={{ backgroundColor: player.logoColor }}
                          >
                            {player.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <CardTitle className="flex items-center gap-2">
                            <User size={18} />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block truncate max-w-[180px] cursor-pointer">{player.name}</span>
                              </TooltipTrigger>
                              <TooltipContent>{player.name}</TooltipContent>
                            </Tooltip>
                          </CardTitle>
                          <CardDescription>{player.role}</CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                              <MoreVertical size={20} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                const url = `${window.location.origin}/players/${player.id}`;
                                navigator.clipboard.writeText(url);
                                toast({ title: 'Profile link copied!', description: 'Player profile URL copied to clipboard.' });
                              }}
                            >
                              <Share2 size={16} className="mr-2" /> Share
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                document.getElementById(`edit-player-${player.id}`)?.click();
                              }}
                            >
                              <Pencil size={16} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                document.getElementById(`delete-player-${player.id}`)?.click();
                              }}
                              className="text-red-600"
                            >
                              <Trash2 size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <div style={{ display: 'none' }}>
                          <EntityActions
                            entity={player}
                            entityType="player"
                            onEdit={updatePlayer}
                            onDelete={deletePlayer}
                            teams={teams}
                            triggerId={`edit-player-${player.id}`}
                          />
                        </div>
                      </div>
                    </div>
                    {player.teamIds && player.teamIds.length > 0 ? (
                      <p className="text-sm text-gray-500 mt-1">
                        Teams: {player.teamIds.map(teamId => teams.find(t => t.id === teamId)?.name).filter(Boolean).join(', ')}
                      </p>
                    ) : null}
                    <UniqueIdDisplay uniqueId={player.uniqueId} />
                  </CardHeader>
                  <CardContent>
                    {/* Add more player details here if needed */}
                  </CardContent>
                  <div className="px-6 pb-4">
                    <Link to={`/players/${player.id}`}>
                      <Button variant="outline" className="w-full">View Details</Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px] text-center">
                    <Checkbox
                      checked={selectedPlayers.length === sortedPlayers.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <SortableTableHeader
                    label="Player Name"
                    sortKey="name"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <SortableTableHeader
                    label="Role"
                    sortKey="role"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[120px]"
                  />
                  <SortableTableHeader
                    label="Team"
                    sortKey="teamId"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <SortableTableHeader
                    label="Unique ID"
                    sortKey="uniqueId"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPlayers.map((player) => {
                  const playerFirstTeamId = player.teamIds && player.teamIds.length > 0 ? player.teamIds[0] : null;
                  const team = teams.find(t => t.id === playerFirstTeamId);
                  const isSelected = selectedPlayers.includes(player.id);
                  return (
                    <TableRow key={player.id}>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectPlayer(player.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell className="font-medium flex items-center gap-2">
                        {player.profilePictureUrl ? (
                          <img
                            src={player.profilePictureUrl}
                            alt={player.name + ' profile picture'}
                            className="w-8 h-8 object-cover rounded-full border border-gray-200 bg-white"
                          />
                        ) : (
                          <div
                            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-white font-bold text-sm uppercase select-none"
                            style={{ backgroundColor: player.logoColor }}
                          >
                            {player.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <Link to={`/players/${player.id}`} className="hover:underline">
                          {player.name}
                        </Link>
                      </TableCell>
                      <TableCell>{player.role}</TableCell>
                      <TableCell>
                        {player.teamIds && player.teamIds.length > 0
                          ? player.teamIds.map(teamId => teams.find(t => t.id === teamId)?.name).filter(Boolean).join(', ')
                          : 'N/A'}
                      </TableCell>
                      <TableCell><UniqueIdDisplay uniqueId={player.uniqueId} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-row items-center gap-2 justify-end">
                          <Link to={`/players/${player.id}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium transition-colors">
                            <Eye size={16} className="mr-1" /> View
                          </Link>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                                <MoreVertical size={20} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  const url = `${window.location.origin}/players/${player.id}`;
                                  navigator.clipboard.writeText(url);
                                  toast({ title: 'Profile link copied!', description: 'Player profile URL copied to clipboard.' });
                                }}
                              >
                                <Share2 size={16} className="mr-2" /> Share
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  document.getElementById(`edit-player-${player.id}`)?.click();
                                }}
                              >
                                <Pencil size={16} className="mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  document.getElementById(`delete-player-${player.id}`)?.click();
                                }}
                                className="text-red-600"
                              >
                                <Trash2 size={16} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <div style={{ display: 'none' }}>
                            <EntityActions
                              entity={player}
                              entityType="player"
                              onEdit={updatePlayer}
                              onDelete={deletePlayer}
                              teams={teams}
                              triggerId={`edit-player-${player.id}`}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        <div className="text-center py-8 text-gray-500">
          {loading.players ? "Loading players..." : "No players found."}
        </div>
      )}
    </div>
  );
};

export default PlayersPage;